import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getVerificationDashboard,
  releaseVendorPayment,
} from "../../redux/actions/verification";
import { toast } from "react-toastify";
import {
  HiOutlineShieldCheck,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
  HiOutlineExclamation,
} from "react-icons/hi";

const AdminVerificationDashboard = () => {
  const dispatch = useDispatch();
  const { verifications, isLoading, total, pages, success, error } =
    useSelector((state) => state.verification);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    dispatch(getVerificationDashboard(statusFilter, paymentFilter, page));
  }, [dispatch, statusFilter, paymentFilter, page]);

  useEffect(() => {
    if (success) {
      toast.success("Payment released successfully");
      dispatch({ type: "clearVerificationSuccess" });
      dispatch(getVerificationDashboard(statusFilter, paymentFilter, page));
      setSelectedVerification(null);
      setAdminNotes("");
    }
    if (error) {
      toast.error(error);
      dispatch({ type: "clearVerificationErrors" });
    }
  }, [success, error]);

  const handleRelease = (verificationId) => {
    dispatch(releaseVendorPayment(verificationId, adminNotes));
  };

  const daysSinceDelivery = (deliveryDate) => {
    if (!deliveryDate) return "-";
    const days = Math.floor(
      (new Date() - new Date(deliveryDate)) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const verificationStatusBadge = (status) => {
    const map = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: HiOutlineClock },
      in_progress: { color: "bg-blue-100 text-blue-800", icon: HiOutlineClock },
      approved: { color: "bg-green-100 text-green-800", icon: HiOutlineCheckCircle },
      failed: { color: "bg-red-100 text-red-800", icon: HiOutlineXCircle },
      disputed: { color: "bg-orange-100 text-orange-800", icon: HiOutlineExclamation },
    };
    const info = map[status] || map.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>
        <Icon className="w-3 h-3" />
        {status?.replace("_", " ")}
      </span>
    );
  };

  const paymentStatusBadge = (status) => {
    const colors = {
      not_ready: "bg-gray-100 text-gray-600",
      ready_for_payment: "bg-teal-100 text-teal-800",
      processing: "bg-blue-100 text-blue-800",
      paid: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>
        {status?.replace(/_/g, " ")}
      </span>
    );
  };

  const checkStatus = (val, label) => {
    const passed = val === true || val === "approved";
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${passed ? "text-green-600" : "text-gray-400"}`}>
        {passed ? (
          <HiOutlineCheckCircle className="w-4 h-4" />
        ) : (
          <HiOutlineXCircle className="w-4 h-4" />
        )}
        {label}
      </span>
    );
  };

  // Summary cards
  const summary = {
    total: verifications?.length || 0,
    readyForPayment: verifications?.filter((v) => v.vendorPaymentStatus === "ready_for_payment").length || 0,
    pending: verifications?.filter((v) => v.verificationStatus === "pending" || v.verificationStatus === "in_progress").length || 0,
    paid: verifications?.filter((v) => v.vendorPaymentStatus === "paid").length || 0,
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen bg-secondary-50">
      <div className="mb-8 pb-6 border-b border-secondary-200">
        <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin</p>
        <h1 className="text-3xl font-bold text-black flex items-center gap-2">
          <HiOutlineShieldCheck className="w-8 h-8" />
          Verification Dashboard
        </h1>
        <p className="text-secondary-500 text-sm mt-1">
          3-way verification: Customer Approval + Payment Received + 5-Day Safety Window
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Verifications", value: total || summary.total, color: "border-[#111111]", icon: HiOutlineShieldCheck },
          { label: "Ready for Payment", value: summary.readyForPayment, color: "border-accent-500", icon: HiOutlineCurrencyDollar },
          { label: "Pending Verification", value: summary.pending, color: "border-yellow-500", icon: HiOutlineClock },
          { label: "Paid", value: summary.paid, color: "border-green-500", icon: HiOutlineCheckCircle },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className={`bg-white border-l-4 ${card.color} shadow-sm p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{card.label}</p>
                  <p className="text-2xl font-bold text-black">{card.value}</p>
                </div>
                <Icon className="w-8 h-8 text-gray-300" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Verification:</span>
          {["", "pending", "in_progress", "approved", "failed", "disputed"].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                statusFilter === s
                  ? "bg-[#111111] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {s?.replace("_", " ") || "All"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="text-sm text-gray-500">Payment:</span>
          {["", "not_ready", "ready_for_payment", "paid"].map((s) => (
            <button
              key={s}
              onClick={() => { setPaymentFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                paymentFilter === s
                  ? "bg-black text-white"
                  : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
              }`}
            >
              {s?.replace(/_/g, " ") || "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111111]" />
        </div>
      ) : (
        <div className="bg-white border border-secondary-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  {[
                    "Order ID",
                    "Product",
                    "Vendor",
                    "Buyer",
                    "Delivery Date",
                    "Customer Approval",
                    "Payment Received",
                    "Days After Delivery",
                    "Verification Status",
                    "Vendor Payment",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {verifications && verifications.length > 0 ? (
                  verifications.map((v) => {
                    const days = daysSinceDelivery(v.deliveryDate);
                    const safetyPassed = typeof days === "number" && days >= (v.safetyWindowDays || 5);
                    const allChecked =
                      (v.customerApproval?.status === "approved") &&
                      v.paymentReceived &&
                      safetyPassed;

                    return (
                      <tr key={v._id} className={`hover:bg-secondary-50 ${allChecked && v.vendorPaymentStatus !== "paid" ? "bg-green-50/50" : ""}`}>
                        <td className="px-3 py-3 text-sm font-mono text-gray-700">
                          {v.order?._id?.slice(-8) || "-"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900 max-w-[150px] truncate">
                          {v.product?.name || "-"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-700">
                          {v.shop?.name || "-"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-700">
                          {v.buyer?.name || "-"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {v.deliveryDate
                            ? new Date(v.deliveryDate).toLocaleDateString("en-IN")
                            : "-"}
                        </td>
                        <td className="px-3 py-3">
                          {checkStatus(v.customerApproval?.status, v.customerApproval?.status === "approved" ? "Approved" : "Pending")}
                        </td>
                        <td className="px-3 py-3">
                          {checkStatus(v.paymentReceived, v.paymentReceived ? "Received" : "Pending")}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`text-sm font-bold ${safetyPassed ? "text-green-600" : "text-orange-500"}`}>
                              {days}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              / {v.safetyWindowDays || 5} days
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {verificationStatusBadge(v.verificationStatus)}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-col gap-0.5">
                            {paymentStatusBadge(v.vendorPaymentStatus)}
                            {v.vendorNetAmount && (
                              <span className="text-xs text-gray-500">
                                ₹{v.vendorNetAmount?.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {allChecked && v.vendorPaymentStatus === "ready_for_payment" ? (
                            <button
                              onClick={() => setSelectedVerification(v)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[#111111] text-white rounded-lg text-xs font-medium hover:bg-[#1a1a1a] transition-colors"
                            >
                              <HiOutlineCurrencyDollar className="w-3.5 h-3.5" />
                              Release
                            </button>
                          ) : v.vendorPaymentStatus === "paid" ? (
                            <span className="text-xs text-green-600 font-medium">Paid ✓</span>
                          ) : (
                            <span className="text-xs text-gray-400">Waiting</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 text-center text-gray-500">
                      No verification records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t">
              <span className="text-sm text-gray-600">
                Page {page} of {pages} ({total} total)
              </span>
              <div className="flex gap-1">
                {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 rounded text-sm ${
                      page === p
                        ? "bg-[#111111] text-white"
                        : "bg-white text-gray-700 border hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3-Way Check Legend */}
      <div className="mt-4 bg-secondary-50 p-4 border border-secondary-200">
        <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-accent-600 mb-3">3-Way Verification Criteria</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <HiOutlineCheckCircle className="w-5 h-5 text-black" />
            <span className="text-secondary-700"><strong>1.</strong> Customer confirms product quality</span>
          </div>
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="w-5 h-5 text-black" />
            <span className="text-secondary-700"><strong>2.</strong> Payment received by platform</span>
          </div>
          <div className="flex items-center gap-2">
            <HiOutlineClock className="w-5 h-5 text-black" />
            <span className="text-secondary-700"><strong>3.</strong> 5-day safety window after delivery</span>
          </div>
        </div>
      </div>

      {/* Release Payment Modal */}
      {selectedVerification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Release Vendor Payment</h3>
            <p className="text-sm text-gray-500 mb-4">
              Order: {selectedVerification.order?._id?.slice(-8)}
            </p>

            <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Order Amount:</span>
                <span className="font-medium">₹{selectedVerification.vendorPaymentAmount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Platform Commission ({selectedVerification.platformCommissionRate}%):</span>
                <span className="text-red-600">-₹{selectedVerification.platformCommission?.toFixed(2)}</span>
              </div>
              {selectedVerification.paymentGatewayFees > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Gateway Fees:</span>
                  <span className="text-red-600">-₹{selectedVerification.paymentGatewayFees?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-semibold">Net Amount to Vendor:</span>
                <span className="text-[#111111] font-bold text-lg">
                  ₹{selectedVerification.vendorNetAmount?.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (optional)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] resize-none"
                rows="2"
                placeholder="Add notes..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setSelectedVerification(null); setAdminNotes(""); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRelease(selectedVerification._id)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#111111] rounded-lg hover:bg-[#1a1a1a]"
              >
                Confirm Release Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVerificationDashboard;
